import React, { useRef, useEffect, useState } from "react";
import Hls from "hls.js";

/**
 * HLS (m3u8) video player. Uses hls.js when supported, native HLS on Safari.
 * Pass manifestUrl (same as current video_url / video_url_a / video_url_b from backend).
 */
const HlsVideoPlayer = ({
  manifestUrl,
  controls = true,
  style = {},
  className = "",
  onError,
  ...rest
}) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!manifestUrl || !videoRef.current) {
      setError(false);
      return;
    }

    setError(false);
    let hls = null;
    const videoEl = videoRef.current;

    const handleNativeError = () => {
      setError(true);
      onError?.(new Event("error"));
    };

    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(manifestUrl);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setError(true);
          onError?.(data);
        }
      });
    } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      videoEl.src = manifestUrl;
      videoEl.addEventListener("error", handleNativeError);
    } else {
      setError(true);
      onError?.(new Error("HLS not supported"));
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
      if (videoEl) {
        videoEl.removeEventListener("error", handleNativeError);
        videoEl.src = "";
      }
    };
  }, [manifestUrl, onError]);

  if (!manifestUrl) {
    return null;
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          background: "#f5f5f5",
          borderRadius: 8,
          color: "#666",
          ...style,
        }}
        className={className}
      >
        Video unavailable
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      controls={controls}
      style={style}
      className={className}
      playsInline
      {...rest}
    />
  );
};

export default HlsVideoPlayer;
